/**
 * CoverageNotice — says, in words and with an icon, when a genealogy answer cannot be trusted as
 * complete (GEN-01 A1, G-D2).
 *
 * The server decides: every genealogy payload carries `coverage` ({source: {status, reason}},
 * status OK | FAILED | NOT_COVERED), `complete`, and for a lot `lot_found` + `message`. This
 * component only renders those fields; it never infers completeness itself.
 */
import React from 'react';
import { Typography } from 'antd';
import StatusIndicator, { StatusNotice } from '@/components/genealogy/StatusIndicator';

const { Text } = Typography;

const STATUS_WORD = { OK: 'read', FAILED: 'failed', NOT_COVERED: 'not covered' };
const STATUS_STATE = { OK: 'ok', FAILED: 'error', NOT_COVERED: 'unknown' };
const sourceLabel = (s) => String(s).replace(/_/g, ' ');

export const LotNotFoundNotice = ({ lotFound, message, lots, className }) =>
  lotFound === false ? (
    <StatusNotice className={className} state="error" title="Lot not found">
      <span>
        {message || 'lot not found in this plant'}
        {lots?.length ? `: ${lots.join(', ')}` : ''}. This is not a “no impact” answer — check the
        lot number and the lot masters before concluding anything.
      </span>
    </StatusNotice>
  ) : null;

/**
 * CoverageSources — the server's per-source list (every source not read OK), with its state and
 * reason. Used inside CoverageNotice and on the Batch Genealogy overview's Traceability Status card.
 */
export const CoverageSources = ({ coverage }) => {
  const notOk = Object.entries(coverage || {}).filter(([, e]) => e?.status !== 'OK');
  if (!notOk.length) return null;
  return (
    <ul style={{ margin: '6px 0 0', padding: 0, listStyle: 'none' }}>
      {notOk.map(([source, e]) => (
        <li key={source} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '2px 0' }}>
          <Text strong style={{ minWidth: 120 }}>{sourceLabel(source)}</Text>
          <StatusIndicator
            state={STATUS_STATE[e.status] || 'unknown'}
            label={STATUS_WORD[e.status] || e.status}
            tooltip={e.reason || undefined}
          />
          {e.reason ? <Text type="secondary">{e.reason}</Text> : null}
        </li>
      ))}
    </ul>
  );
};

const CoverageNotice = ({ coverage, complete, className }) => {
  if (complete !== false) return null;
  return (
    <StatusNotice className={className} state="warning" title="Incomplete">
      <Text type="secondary">
        Not every source behind this answer was read, so an empty or low count here does not mean
        “no impact”.
      </Text>
      <CoverageSources coverage={coverage} />
    </StatusNotice>
  );
};

export default CoverageNotice;
